/**
 * WidthWrapper
 *
 * .ist-js__widthWrapper クラスを持つ要素に対して、
 * 要素の左端から body の右端（スクロールバーを含む）までの距離を計算し、
 * その幅を要素の幅に設定します。
 * リサイズ時にも再計算されます。
 *
 * 使用例:
 * document.addEventListener('DOMContentLoaded', () => {
 *   const wrapper = new WidthWrapper();
 *
 *   // クリーンアップが必要な場合
 *   // wrapper.destroy();
 * });
 *
 * 追加仕様01
 * .ist-js__widthWrapper--notSp クラスを持つ場合
 * 960px 以下の場合、width を 100% にする
 */
class WidthWrapper {
  private readonly selector: string;

  constructor(elementSelector: string = '.ist-js__widthWrapper') {
    this.selector = elementSelector;
    this.init();
  }

  private init(): void {
    const elements = document.querySelectorAll(this.selector);
    if (!elements.length) {
      console.warn(`${this.selector} 要素が見つかりません`);
      return;
    }

    elements.forEach((element) => {
      const target = element as HTMLElement;

      // 初期幅の調整
      this.adjustWidth(target);

      // デバウンスされたリサイズリスナーを作成
      const debouncedResize = this.debounce(
        () => this.adjustWidth(target),
        100
      );

      // イベントリスナーを追加
      window.addEventListener('resize', debouncedResize);

      // クリーンアップ用にデータ属性に関数参照を保存
      (target as any)._resizeListener = debouncedResize;
    });
  }

  private adjustWidth(element: HTMLElement): void {
    const bodyWidth = document.body.clientWidth;

    // 追加仕様01: .ist-js__widthWrapper--notSp クラスが存在し、960px 以下の場合は width を 100% に設定
    if (
      element.classList.contains('ist-js__widthWrapper--notSp') &&
      bodyWidth <= 960
    ) {
      element.style.width = '100%';
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const elementLeft = elementRect.left + window.scrollX;
    const distanceToRight = bodyWidth - elementLeft;
    element.style.width = `${distanceToRight}px`;
  }

  private debounce(func: () => void, wait: number): () => void {
    let timeout: number | undefined;
    return () => {
      clearTimeout(timeout);
      timeout = window.setTimeout(func, wait);
    };
  }

  public destroy(): void {
    const elements = document.querySelectorAll(this.selector);
    elements.forEach((element) => {
      const target = element as HTMLElement;

      // 登録されたリスナーを取得
      const resizeListener = (target as any)._resizeListener;

      if (resizeListener) {
        window.removeEventListener('resize', resizeListener);
        delete (target as any)._resizeListener;
      }

      // 幅をリセット
      target.style.width = '';
    });
  }
}

export default WidthWrapper;
